
import {FaEdit, FaTrash, FaPlus} from 'react-icons/fa'
import {useEffect, useState} from "react"
import {Link} from "react-router-dom"
import {Subject} from "models/model"
import {SubjectDelete, SubjectGetPaging} from "services/subjectService"
import ConfirmDialog from "popup/ConfirmDialog";
import { toast } from "react-hot-toast";
import LoadingOverlay from "components/Static/LoadingOverlay";
import Pagination from "components/Pagination";
import { Edit, Plus, Trash } from 'lucide-react';

export default function SubjectsPage() {
    const [subjects, setSubjects] = useState<Subject[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)
    const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null)
    const [searchText, setSearchText] = useState<string>("")
    const [subjectType, setSubjectType] = useState<string>("")
    const [pagination, setPagination] = useState({
        pageIndex: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 1
    })

    const openConfirm = (subjectId: string) => {
        setSelectedSubjectId(subjectId)
        setIsConfirmOpen(true)
    }

    const handleDelete = async () => {
        if (selectedSubjectId === null) return
        try {
            await SubjectDelete(selectedSubjectId)
            setSubjects(prev => prev.filter(s => s.id !== selectedSubjectId))
            toast.success("Xóa môn học thành công")
            fetchSubjects(pagination.pageIndex, true)
        } catch (error : any) {
            toast.error(error.message || "Đã có lỗi xảy ra khi xóa môn học")
        }
    }

    const handleSearch = () => {
        fetchSubjects(1)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const fetchSubjects = async (pageIndex: number, keepPageIndex: boolean = false) => {
        setIsLoading(true)
        try {
            const res = await SubjectGetPaging({
                pageIndex: keepPageIndex ? pagination.pageIndex : pageIndex,
                pageSize: pagination.pageSize,
                freeTextSearch: searchText,
                type: parseInt(subjectType) || undefined
            })
            setSubjects(res.result.items)
            setPagination(prev => ({
                ...prev,
                pageIndex: keepPageIndex ? prev.pageIndex : pageIndex,
                totalRecords: res.result.totalRecord,
                totalPages: Math.ceil(res.result.totalRecord / pagination.pageSize)
            }))
        } catch (error) {
            console.error('Failed to fetch subjects:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const handlePageChange = (newPageIndex: number) => {
        if (newPageIndex < 1 || newPageIndex > pagination.totalPages) return
        fetchSubjects(newPageIndex)
    }

    useEffect(() => {
        fetchSubjects(1)
    }, [subjectType])

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h1 className="text-3xl font-bold text-gray-800">Quản lý Môn học</h1>
            <div className="md:flex md:justify-between md:items-center mb-6 mt-6">
                <Link
                    to="/subjects/new"
                    className="flex items-center px-4 py-2 mb-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    <Plus className="mr-2"/>
                    Thêm môn học mới
                </Link>
                <div className="md:flex md:justify-start md:items-center">

                    <div className="md:flex md:items-center mb-3 md:mr-4">
                        <select
                            className="md:min-w-max w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                            value={subjectType}
                            onChange={(e) => setSubjectType(e.target.value)}
                        >
                            <option value="">Tất cả loại môn học</option>
                            <option value="1">Lý thuyết</option>
                            <option value="2">Thực hành</option>
                        </select>
                    </div>

                    <div className="flex items-center space-x-2 mb-3">
                        <input
                            type="text"
                            placeholder="Tìm kiếm môn học..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                        />
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Tìm
                        </button>
                    </div>
                </div>

            </div>

            {isLoading ? <LoadingOverlay message="Đang tải dữ liệu..."/> : (
                <div className="mx-auto">
                    <div className="grid grid-cols-1 gap-8">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="w-full overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">STT</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tên
                                            môn học
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mã
                                            môn học
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loại
                                            môn học
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Thao
                                            tác
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {subjects.map((subject, index) => (
                                        <tr key={subject.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                {(pagination.pageIndex - 1) * pagination.pageSize + index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-normal">
                                                <div
                                                    className="font-medium text-gray-900 break-words">{subject.name}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                {subject.code || '--'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                {subject.type === 1 ? 'Lý thuyết' : 'Thực hành'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                <Link
                                                    to={`/subjects/${subject.id}/edit`}
                                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                                >
                                                    <Edit className="inline mr-1"/> Sửa
                                                </Link>
                                                <button
                                                    onClick={() => openConfirm(subject.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    <Trash className="inline mr-1"/> Xóa
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            pagination={pagination}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                            label="môn hoc"
                        />
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={isConfirmOpen}
                title="Xác nhận xóa"
                description="Bạn có chắc chắn muốn xóa môn học này không?"
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleDelete}
            />
        </div>
    )
}